<script setup>
import {
  computed,
  ref,
  watch,
  nextTick,
} from 'vue';

import Chart from 'primevue/chart';

import { storeToRefs } from 'pinia';

import Card from 'primevue/card';
import Column from 'primevue/column';
import DataTable from 'primevue/datatable';
import SelectButton from 'primevue/selectbutton';

import { withCommas } from '@/utils/numbers';

import useTrackingLinksStore from '@/stores/tracking-links';

import {
  doughnutChartOptions,
  doughnutBackgroundColors,
  doughnutBackgroundColorsHover,
} from '@/composables/useChartConfigs';

const props = defineProps({
  label: {
    type: String,
    default: '',
  },

  dataset: {
    type: String,
    required: true,
  },
});

const {
  isFetching,
  analytics,
} = storeToRefs(useTrackingLinksStore());

// Delay chart rendering to ensure DOM is ready after transition
const chartReady = ref(false);
let chartTimer;
watch(isFetching, async (fetching) => {
  clearTimeout(chartTimer);
  if (!fetching) {
    chartReady.value = false;
    await nextTick();
    chartTimer = setTimeout(() => {
      chartReady.value = true;
    }, 100);
  } else {
    chartReady.value = false;
  }
}, { immediate: true });

const title = computed(() => `Views By ${props.label || props.dataset}`);
const prettyDatasetName = computed(() => {
  switch (props.dataset) {
    case 'browser':
      return 'Browser';
    case 'os':
      return 'Operating System';
    case 'device':
      return 'Device Type';
    default:
      return props.dataset.charAt(0).toUpperCase() + props.dataset.slice(1);
  }
});

const view = ref('chart');

const viewOptions = ref([{
  icon: 'far fa-chart-line',
  value: 'chart',
  tooltip: 'Chart View',
}, {
  icon: 'far fa-table-layout',
  value: 'table',
  tooltip: 'Table View',
}]);

const getData = () => ({
  browser: analytics?.value?.byBrowser,
  os: analytics?.value?.byOS,
  device: analytics?.value?.byDevice,
}[props.dataset] || null);

/**
 * Get list of labels for chart, with sorting provided by API.
 * @returns {Array<string>} List of labels for the chart.
 */
const getChartLabels = () => {
  const data = getData();
  if (!data) {
    console.warn('No data for provided dataset:', props.dataset); // eslint-disable-line no-console
    return [];
  }

  return data.map((rec) => rec.key || 'Unknown');
};

/**
 * Get actual chart data per entry, using the provided property name.
 * @param {string} property - The property to use for chart data, e.g. 'doc_count' or 'unique_count'.
 * @returns {Array<number>} List of values for the chart data.
 */
const getChartData = (property = 'doc_count') => {
  const data = getData();
  if (!data) {
    console.warn('No data for provided dataset:', props.dataset); // eslint-disable-line no-console
    return [];
  }

  return data.map((rec) => rec[property]);
};

const chartData = computed(() => {
  return {
    labels: getChartLabels(),
    datasets: [{
      label: 'Total Visits',
      data: getChartData('doc_count'),
      backgroundColor: doughnutBackgroundColors,
      hoverBackgroundColor: doughnutBackgroundColorsHover,
    }, {
      label: 'Unique Visits',
      data: getChartData('unique_count'),
      backgroundColor: doughnutBackgroundColors,
      hoverBackgroundColor: doughnutBackgroundColorsHover,
    }],
  };
});

const chartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,

  plugins: {
    legend: {
      display: true,
    },

    tooltip: doughnutChartOptions.value.plugins.tooltip,
    datalabels: doughnutChartOptions.value.plugins.datalabels,
  },
}));

const tableData = computed(() => {
  const data = {
    browser: analytics?.value?.byBrowser,
    os: analytics?.value?.byOS,
    device: analytics?.value?.byDevice,
  }[props.dataset];

  if (!data) {
    console.warn('No data for provided dataset:', props.dataset); // eslint-disable-line no-console
    return [];
  }

  return data.map((rec) => ({
    key: rec.key || 'Unknown',
    totalVisits: withCommas(rec.doc_count),
    uniqueVisits: withCommas(rec.unique_count),
  }));
});
</script>

<template>
  <Card
    pt:root:class="analytics-card analytics-card__total-visits-map shadow-2 mb-3 relative"
    pt:body:class="w-full"
  >
    <template #content>
      <div v-if="isFetching" class="flex flex-column justify-content-center align-items-center">
        <Skeleton height="1rem" width="6rem" class="mb-2" />
        <Skeleton height="4rem" width="4rem" />
      </div>

      <Transition name="fade-in-out" mode="out-in">
        <div v-if="!isFetching" :key="view">
          <SelectButton
            class="analytics-card__view-type-toggle"
            v-model="view"
            :options="viewOptions"
            option-label="value"
            option-value="value"
            aria-labelledby="custom"
            data-entity="analytics-trackingLink-overTimeViewSwitcher"
            data-test="analytics-trackingLink-overTimeViewSwitcher"
          >
            <template #option="slotProps">
              <font-awesome-icon
                :icon="slotProps.option.icon"
                v-tooltip="slotProps.option.tooltip"
              />
            </template>
          </SelectButton>

          <h5 class="text-center p-0 m-0 mb-2 capitalize">
            {{ title }}
          </h5>

          <Chart
            v-if="chartReady && view === 'chart'"
            type="doughnut"
            :data="chartData"
            :options="chartOptions"
            :height="300"
          />

          <DataTable
            v-else-if="view === 'table'"
            :value="tableData"
            pt:wrapper:class="shadow-none"
            :aria-label="title"
            scrollHeight="300px"
            :data-test="`analytics-trackingLink-visitsTable-${dataset}`"
            :data-entity="`analytics-trackingLink-visitsTable-${dataset}`"
          >
            <Column
              field="key"
              :header="prettyDatasetName"
            />

            <Column
              field="totalVisits"
              header="Total Visits"
              class="analytics-card__table-column-small"
            />

            <Column
              field="uniqueVisits"
              header="Unique Visits"
              class="analytics-card__table-column-small"
            />
          </DataTable>
        </div>
      </Transition>
    </template>
  </Card>
  <!-- End Visits Over Time -->
</template>

<style>
.analytics-card__view-type-toggle {
  position: absolute;
  right: 0.5rem;
  top: 0.5rem;
  z-index: 1;
}

.p-selectbutton .p-button {
  min-width: unset;
}
</style>
